import React, { FC } from "react";
import { WrapperSection } from "@ldp-web/component-library/ui/atoms/section";
import { GalleryCardScroller } from "@ldp-web/component-library/ui/organisms/galleries/gallery-card-scroller";
import type { ScrollerContentType } from "@ldp-web/component-library/ui/organisms/galleries/gallery-card-scroller/gallery-card-scroller.types";
import { useSDMRouterContext } from "@ldp/sdm-contexts";
import type { IHeaderGroupComponentProps } from "@ldp/common-types";
import { useRedirectLink } from '../../utils';
import { useSimpleTranslation } from "@ldp/simple-i18n";
import { HeaderGroupComponent } from "../header-group/header-group.component";

type ScrollOverflow = "ALWAYS" | "NEVER" | "ONLY_FOR_SMALL_SCREENS";
interface IGalleryCardScrollerComponentProps {
  scrollerContent: ScrollerContentType[];
  scrollOverflow: ScrollOverflow;
  headerGroup?: IHeaderGroupComponentProps;
}

interface IGroupContentItem {
  image?: { url?: string } | string;
  callToAction?: {
    url?: string;
    text?: string;
  };
  styling?: unknown;
  [key: string]: unknown;
}

const parseContent = (
  scrollerContent: ScrollerContentType[],
  redirectToPage: (redirectUrl?: string) => void
): ScrollerContentType[] => {
  return scrollerContent.map((item) => ({
    ...item,
    groupContent: item.groupContent?.map((content: IGroupContentItem) => {
      let imageUrl = "";
      if (typeof content.image === "object" && content.image !== null) {
        imageUrl = content.image.url ?? "";
      } else if (typeof content.image === "string") {
        imageUrl = content.image;
      }
      const callToActionUrl = content.callToAction?.url;
      const callToActionText = content.callToAction?.text ?? "";

      return {
        ...content,
        image: imageUrl,
        callToActionHandler: () => {
          redirectToPage(callToActionUrl);
        },
        label: callToActionText,
        callToAction: undefined,
        styling: content.styling ?? null,
        linkUrl: callToActionUrl,
      };
    }),
  })) as ScrollerContentType[];
};

export const GalleryCardScrollerComponent: FC<
  IGalleryCardScrollerComponentProps
> = ({ scrollerContent, scrollOverflow, headerGroup }) => {
  const i18n = useSimpleTranslation();
  const { router } = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const { headline, callToAction } = headerGroup || {};

  const headerGroupProps: IHeaderGroupComponentProps = headerGroup ?? {
    headline: {
      text: headline?.text || "",
      variantId: "MEDIUM_03",
      callToAction,
      clickHandler: redirectToPage,
    },
  };

  return (
    <WrapperSection>
      <HeaderGroupComponent {...headerGroupProps} noWrapper />
      <GalleryCardScroller
        callToActionHandler={redirectToPage}
        i18nskip={i18n("skip")}
        scrollOverflow={scrollOverflow}
        scrollerContent={parseContent(scrollerContent, redirectToPage)}
        skipLinkLabel={headerGroupProps.headline.text || i18n("a11ySection")}
      />
    </WrapperSection>
  );
};
